import * as React from 'react'
import { Line, Bar, LinePath } from '@vx/shape'
import { GridRows } from '@vx/grid'
import { scaleTime, scaleLinear, scaleOrdinal } from '@vx/scale'
import { withTooltip } from '@vx/tooltip'
import { localPoint } from '@vx/event'
import { extent, max, bisector } from 'd3-array'
import * as colors from 'styles/colors-export.css'
import { GraphTooltip } from '../GraphTooltip'
import LinedTable from 'components/Tables/LinedTable'
import * as sText from 'components/Text/Text.scss'
import { AxisBottom, AxisRight } from '@vx/axis'
import { round } from 'lodash'
import { Legend } from '@vx/legend'
import { PatternCircles } from '@vx/pattern'
import { Group } from '@vx/group'
import moment from 'moment'

export interface IGraphLineCompareProps {
  data1: {
    date: string
    value: number
  }[]
  data2: {
    date: string
    value: number
  }[]
  width: number
  height: number
  label: string
  unit?: string
  dateFormat?: string
}

// accessors
const xAccessor = d => moment(d.date)
const yAccessor = d => d.value
const bisectDate = bisector(d => moment(d.date)).left

const LegendShape = props => {
  return (
    <svg width={props.width} height={props.height}>
      {props.label.value.pattern}
      <rect
        x={0}
        y={0}
        width={props.width}
        height={props.height}
        fill={`url(#${props.label.value.id})`}
      />
    </svg>
  )
}

class GraphLineCompareComponent extends React.Component<any> {
  handleTooltip = ({ event, xScale, yScale }) => {
    const { showTooltip, data1, data2 } = this.props
    const { x } = localPoint(event)
    const x0 = xScale.invert(x)
    const index = bisectDate(data1, x0, 1)
    const d1 = data1[index]
    const d2 = data2[index]

    showTooltip({
      tooltipData: [d1, d2],
      tooltipLeft: xScale(xAccessor(d1)),
      tooltipTop: [yScale(d1.value), yScale(d2.value)],
    })
  }
  render() {
    const {
      width,
      height,
      margin = {
        left: 0,
        top: 40,
        right: 40,
        bottom: 20,
      },
      hideTooltip,
      tooltipData,
      tooltipTop,
      tooltipLeft,
      label,
      unit,
      data1,
      data2,
      dateFormat,
    } = this.props
    if (width < 10) return null

    // bounds
    const xMax = width - margin.left - margin.right
    const yMax = height - margin.top - margin.bottom

    // scales
    const xScale = scaleTime({
      range: [0, xMax],
      domain: extent(data1, xAccessor),
    })
    const yScale = scaleLinear({
      range: [yMax, 0],
      domain: [0, max(data1, yAccessor) * 1.5],
      nice: true,
    })

    const legendPattern = scaleOrdinal({
      domain: ['This period', 'Previous period'],
      range: [
        {
          id: 'pattern1',
          pattern: (
            <PatternCircles id="pattern1" height={1} width={1} fill={colors.tertiary} complement />
          ),
        },
        {
          id: 'pattern2',
          pattern: <PatternCircles id="pattern2" height={5} width={5} fill={colors.tertiary} />,
        },
      ],
    })

    const tooltipCurrentPeriodVal = tooltipData && yAccessor(tooltipData[0])
    const tooltipPrevioustPeriodVal = tooltipData && yAccessor(tooltipData[1])
    const tooltipIncrease = tooltipPrevioustPeriodVal
      ? Math.round((tooltipCurrentPeriodVal / tooltipPrevioustPeriodVal) * 100 - 100)
      : 'NA'

    return (
      <div>
        <div
          style={{
            position: 'absolute',
            top: 0,
            display: 'flex',
            justifyContent: 'center',
            fontSize: '12px',
          }}
        >
          <Legend
            direction="row"
            labelMargin="0"
            shapeMargin="2px 4px 2px 0px"
            itemMargin="0 15px 0 0"
            scale={legendPattern}
            shapeWidth={15}
            shape={LegendShape}
          />
        </div>
        <svg width={width} height={height}>
          <Group top={margin.top}>
            <GridRows
              lineStyle={{ pointerEvents: 'none' }}
              scale={yScale}
              width={xMax}
              stroke="rgba(0,0,0,0.1)"
            />
            <LinePath
              data={data1}
              xScale={xScale}
              yScale={yScale}
              x={xAccessor}
              y={yAccessor}
              strokeWidth={2}
              stroke={colors.tertiary}
            />
            <LinePath
              data={data2}
              xScale={xScale}
              yScale={yScale}
              x={xAccessor}
              y={yAccessor}
              strokeWidth={2}
              stroke={colors.tertiary}
              strokeDasharray="2,2"
            />
            <Bar
              x={0}
              y={0}
              width={width}
              height={height}
              fill="transparent"
              rx={14}
              data={data1}
              onTouchStart={data => event => this.handleTooltip({ event, xScale, yScale })}
              onTouchMove={data => event => this.handleTooltip({ event, xScale, yScale })}
              onMouseMove={data => event => this.handleTooltip({ event, xScale, yScale })}
              onMouseLeave={data => event => hideTooltip()}
            />
            <AxisRight hideAxisLine hideTicks scale={yScale} numTicks={6} left={xMax} top={0} />
            <AxisBottom
              scale={xScale}
              top={yMax}
              numTicks={5}
              stroke="rgba(0,0,0,0.1)"
              tickStroke="rgba(0,0,0,0.1)"
              tickFormat={val => moment(val).format(dateFormat)}
              tickLabelProps={(value, index) => ({
                fontSize: 10,
              })}
            />
            {tooltipData && (
              <g>
                <Line
                  from={{ x: tooltipLeft, y: 0 }}
                  to={{ x: tooltipLeft, y: yMax }}
                  stroke="rgba(0,0,0,0.1)"
                  strokeWidth={1}
                  style={{ pointerEvents: 'none' }}
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop[0] + 1}
                  r={4}
                  fill="black"
                  fillOpacity={0.1}
                  stroke="black"
                  strokeOpacity={0.1}
                  strokeWidth={2}
                  style={{ pointerEvents: 'none' }}
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop[0]}
                  r={4}
                  fill={colors.primary}
                  stroke="white"
                  strokeWidth={2}
                  style={{ pointerEvents: 'none' }}
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop[1] + 1}
                  r={4}
                  fill="black"
                  fillOpacity={0.1}
                  stroke="black"
                  strokeOpacity={0.1}
                  strokeWidth={2}
                  style={{ pointerEvents: 'none' }}
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop[1]}
                  r={4}
                  fill={colors.primary}
                  stroke="white"
                  strokeWidth={2}
                  style={{ pointerEvents: 'none' }}
                />
              </g>
            )}
          </Group>
        </svg>
        {tooltipData && (
          <div>
            <GraphTooltip top={max(tooltipTop) - 12} left={tooltipLeft}>
              <LinedTable>
                <div>
                  <div>Date:</div>
                  <div>{xAccessor(tooltipData[0]).format('D/M/Y')}</div>
                </div>
                <div>
                  <div>{label}:</div>
                  <div>
                    {round(yAccessor(tooltipData[0]), 2)}
                    {unit}
                  </div>
                </div>
                <div>
                  <div>Change:</div>
                  <div className={tooltipIncrease < 0 ? sText.warn : sText.green}>
                    {tooltipIncrease < 0 ? '' : '+'}
                    {tooltipIncrease}%
                  </div>
                </div>
              </LinedTable>
            </GraphTooltip>
          </div>
        )}
      </div>
    )
  }
}

export const GraphLineCompare = withTooltip(GraphLineCompareComponent) as React.SFC<
  IGraphLineCompareProps
>



// WEBPACK FOOTER //
// src/features/CallAnalytics/GraphLineCompare/GraphLineCompare.tsx